import { API_URL as API, BACKEND_URL } from '../utils/config';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';


const Cart = () => {
  const { t } = useLanguage();
  const { cart, removeFromCart, clearCart } = useCart();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('posten_brev');

  const shippingOptions = [
    { id: 'posten_brev', name: 'Posten - Stort brev med sporing', price: 46, days: '2-3 dager' },
    { id: 'posten_pakke', name: 'Posten - Norgespakke (hjemlevering)', price: 89, days: '1-3 dager' },
    { id: 'postnord', name: 'PostNord - MyPack Home', price: 129, days: '1-2 dager' },
    { id: 'helthjem', name: 'Helthjem - Hjemlevering', price: 89, days: '1-2 dager' }
  ];

  useEffect(() => {
    if (cart.length > 0) {
      fetchProductDetails();
    }
  }, [cart]);

  const fetchProductDetails = async () => {
    try {
      const productIds = [...new Set(cart.map(item => item.product_id))];
      const responses = await Promise.all(
        productIds.map(id => axios.get(`${API}/products/${id}`))
      );
      const productsMap = {};
      responses.forEach(response => {
        productsMap[response.data.id] = response.data;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Failed to fetch product details:', error);
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await removeFromCart(itemId);
      toast.success(t('removedFromCart'));
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => {
      const product = products[item.product_id];
      if (product) {
        return total + (product.price_nok * item.quantity);
      }
      return total;
    }, 0);
  };

  const getShippingCost = () => {
    const selectedOption = shippingOptions.find(opt => opt.id === shippingMethod);
    return selectedOption ? selectedOption.price : 0;
  };

  const calculateTotal = () => {
    return (calculateSubtotal() + getShippingCost()).toFixed(2);
  };

  const handleCheckout = async () => {
    try {
      setLoading(true);
      const items = cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        shade: item.shade,
        price: products[item.product_id]?.price_nok || 0
      }));

      // Add shipping as a line item
      const selectedShipping = shippingOptions.find(opt => opt.id === shippingMethod);
      items.push({
        product_id: 'shipping',
        quantity: 1,
        name: selectedShipping.name,
        price: selectedShipping.price
      });

      const originUrl = window.location.origin;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${API}/payment/checkout`,
        { items, origin_url: originUrl, shipping_method: shippingMethod },
        { headers }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24 flex flex-col items-center justify-center" data-testid="empty-cart">
        <h1 className="text-3xl md:text-lg font-sans font-bold uppercase tracking-tight text-[#1A1A1A] mb-6">
          {t('emptyCart')}
        </h1>
        <Link 
          to="/shop" 
          className="bg-[#1A1A1A] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all duration-300"
          data-testid="continue-shopping-btn"
        >
          {t('continueShopping')}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24" data-testid="cart-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <h1 className="text-5xl md:text-7xl font-serif tracking-tight leading-none text-[#1A1A1A] mb-12">
          {t('yourCart')}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
            {cart.map(item => {
              const product = products[item.product_id];
              if (!product) return null;

              return (
                <div key={item.id} className="bg-white p-6 border border-[#E5E5E5] flex gap-6" data-testid={`cart-item-${item.id}`}>
                  <img 
                    src={product.image_url} 
                    alt={product.name_en} 
                    className="w-24 h-24 object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-base font-serif text-[#1A1A1A] mb-2">
                      {product.name_en}
                    </h3>
                    {item.shade && (
                      <p className="text-xs text-[#8A8A8A] mb-2">
                        {t('selectShade')}: {item.shade}
                      </p>
                    )}
                    <p className="text-sm text-[#1A1A1A]">
                      {product.price_nok} NOK x {item.quantity}
                    </p>
                  </div>
                  <button 
                    onClick={() => handleRemove(item.id)}
                    className="text-[#991B1B] hover:text-[#7F1D1D] transition-colors"
                    data-testid={`remove-item-${item.id}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-8 border border-[#E5E5E5] sticky top-32" data-testid="order-summary">
              <h2 className="text-xl font-serif text-[#1A1A1A] mb-6">
                {t('subtotal')}
              </h2>

              {/* Shipping Options */}
              <div className="mb-6">
                <h3 className="text-sm font-sans font-bold uppercase tracking-wider text-[#1A1A1A] mb-3">
                  Velg Frakt
                </h3>
                <div className="space-y-2">
                  {shippingOptions.map(option => (
                    <label 
                      key={option.id}
                      className={`flex items-center justify-between p-3 border cursor-pointer transition-colors ${
                        shippingMethod === option.id 
                          ? 'border-[#1A1A1A] bg-gray-50' 
                          : 'border-[#E5E5E5] hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="shipping"
                          value={option.id}
                          checked={shippingMethod === option.id}
                          onChange={(e) => setShippingMethod(e.target.value)}
                          className="w-4 h-4"
                        />
                        <div>
                          <p className="text-sm font-medium text-[#1A1A1A]">{option.name}</p>
                          <p className="text-xs text-[#8A8A8A]">{option.days}</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-[#1A1A1A]">{option.price} NOK</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-4 mb-6 border-t border-[#E5E5E5] pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-[#8A8A8A]">{t('subtotal')}</span>
                  <span className="text-[#1A1A1A]" data-testid="subtotal-amount">{calculateSubtotal().toFixed(2)} NOK</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#8A8A8A]">Frakt</span>
                  <span className="text-[#1A1A1A]">{getShippingCost()} NOK</span>
                </div>
                <div className="border-t border-[#E5E5E5] pt-4 flex justify-between font-serif">
                  <span className="text-[#1A1A1A]">{t('total')}</span>
                  <span className="text-[#1A1A1A]" data-testid="total-amount">{calculateTotal()} NOK</span>
                </div>
              </div>
              <button 
                onClick={handleCheckout}
                disabled={loading}
                className="w-full bg-[#1A1A1A] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all duration-300 disabled:opacity-50"
                data-testid="checkout-btn"
              >
                {loading ? 'Processing...' : t('checkout')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;